import React, { useState, useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from 'uuid';

// MUI Components
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Modal,
  Card,
  CardContent,
  Paper,
  Tab,
  Tabs,
  CircularProgress
} from "@mui/material";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CachedIcon from '@mui/icons-material/Cached';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile'
import UploadIcon from '@mui/icons-material/Upload';
import StopIcon from '@mui/icons-material/Stop';
import BugReportIcon from '@mui/icons-material/BugReport';
import logo from '../../../icons/LOGO.svg'
import ArticleIcon from '@mui/icons-material/Article';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

// Custom Components
import SectionSourceCard from "../../../components/SectionSourceCard";
import AsynchronousActionButton from "../../../components/Buttons/AsynchronousActionButton";
import { SecondaryButtonFactory } from "../../../components/Buttons/SecondaryButton";
import GradientCircularProgressIndicator from "../../../components/GradientCircularProgressIndicator";
import { SectionSelectionModal } from "../../CreateDocumentPage/steps/modals/SelectDocumentSectionToBeGenerated";
import { sectionSelectionStorage } from '../../../services/SectionSelectionStorage';
import CustomTable from "../../../components/CustomTable";
import AssignTablesModal from "../modals/AssignTablesModal";
import SourceSelectionModal from "../../CreateDocumentPage/steps/SourceSelectionModal";

// Example JSONs
import documentSpecificMRTExample from "../../../utils/ExampleJSONs/documentSpecific/documentSpecificMRTExample.json";

// Utils
// Table Assignment Utils
import { TableAssignmentMapper } from '../../../utils/TableAssignmentMapper';

// Application Program Interfaces
import {
  regenerateSelectionMRT,
  getChunkFromId,
  getChunksFromIds,
  getDocumentFromId,
  getSourceDataReferences,
  insertMixedContent,
  insertMixedContentWithFormatting,
  captureCurrentFormatting,
  generatePresignedUrl,
  getParagraphStyleInfo,// Add this if you need Document Builder API functionality 
  getAllMRTTables,
  uploadMultipleFilesUpdated,
  extractClassifyAndIngestCelery,
  documentsRegenerateV1,
  getAllMRTReports
} from "../../../utils/utils";
import { formatToolText } from "../../../utils/stringFormattingUtils";


// Contexts
import { useAuthInfo } from '@propelauth/react'
import { useTheme } from "@mui/material/styles";

// Utils
import { taskPoller } from '../../../utils/taskPoller';
import { getMRTTables } from '../../../utils/utils';


// Source Document Status
import { useSourceDocumentStatus } from '../../../hooks/useSourceDocumentStatus';

// String Utils
import { formatTitleFromDeliminatedString } from "../../../utils/stringFormattingUtils";
import SelectDocumentSectionToBeGenerated from "../../CreateDocumentPage/steps/modals/SelectDocumentSectionToBeGenerated";


// Helper function to extract content from document_synopsis tags and remove thinking tags
const extractDocumentSynopsis = (synopsisText) => {
  if (!synopsisText) return '';
  
  let content = synopsisText;
  
  // First, check if the text contains complete document_synopsis tags
  const completeMatch = synopsisText.match(/<document_synopsis>(.*?)<\/document_synopsis>/s);
  
  if (completeMatch && completeMatch[1]) {
    // Use content between the complete document_synopsis tags
    content = completeMatch[1];
  } else {
    // If no complete tags, remove any orphaned opening/closing tags
    content = content.replace(/<document_synopsis>\s*/g, '');
    content = content.replace(/\s*<\/document_synopsis>/g, '');
  }
  
  // Remove any content within <thinking> </thinking> tags
  content = content.replace(/<thinking>.*?<\/thinking>/gs, '');
  
  // Trim the content
  content = content.trim();
  
  // Truncate to 200 characters and add ellipsis
  if (content.length > 500) {
    content = content.substring(0, 500) + '...';
  }
  
  // Return the cleaned content
  console.log('cleaned content:', content)
  return content;
};

const getSectionsData = async (id, authInfo, setLoading, setError) => {

  const org = authInfo.orgHelper.getOrgs()[0].orgId;
  const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);

  const mrtOutline = await getDocumentFromId(id, orgAccessToken)

  console.log('mrtOutline', mrtOutline)

  const rawSections = Array.isArray(mrtOutline.document.mrt) ? mrtOutline.document.mrt : mrtOutline.document.mrt.sections

  const sectionPromises = rawSections.map(async (section) => {
    // Ids associated with ncr documents stored in the ncr_documents collection
    let sourceDataReferenceIdsArray = []
    // Ids associated with chunks stored in the mrt_example_chunks collection
    let chunkIdsArray = []
    // Ids associated with tables stored in the mrt_tables collection
    let mrtTableIdsArray = []

    const rawSectionSources = section.relevant_chunk_ids ? section.relevant_chunk_ids.map(chunk_id => chunk_id) : section.relevant_chunks.map(chunk => chunk.chunk_id)
    
    chunkIdsArray = rawSectionSources

    section.rules.forEach(rule => {

      // Check if the rule has a source data reference property AND that the property is not empty
      if (rule.source_data_references && rule.source_data_references.length > 0) {

        // Source data reference property's are arrays of objects that contain either a chunk_id or an identifier
        rule.source_data_references.forEach(sourceDataReference => {

          // If the object has a chunk_id property, add it to the chunkIdsArray
          if (sourceDataReference.chunk_id) {
            chunkIdsArray.push(sourceDataReference.chunk_id)
          } else {

            // If the object has an identifier property, add it to the sourceDataReferenceIdsArray
            sourceDataReferenceIdsArray.push(sourceDataReference.identifier)
          }
        })
      } 
      // Check if the rule has a rule_parameters object AND that the object has a tfl_id property
      else if (rule.rule_parameters && rule.rule_parameters.tfl_id) {

        // Add the table id to the mrtTableIdsArray
        mrtTableIdsArray.push(rule.rule_parameters.tfl_id)
      }
    })

    // Table Objects whose id are the rule_parameter object's tfl_id
    const mrtTables = await getMRTTables(setLoading, setError, mrtTableIdsArray, orgAccessToken)

    // Chunk Objects whose id are the source_data_reference object's chunk_id
    const sectionSources = await getChunksFromIds(setLoading, setError, chunkIdsArray, orgAccessToken)

    // NCR Document Objects whose id are the source_data_reference object's identifier
    const sourceDataReferences = await getSourceDataReferences(setLoading, setError, sourceDataReferenceIdsArray, orgAccessToken)

    console.log('mrtTables', mrtTables)
    console.log('sourceDataReferences', sourceDataReferences)
    console.log('sectionSources', sectionSources)

    return {
      title: section.title,
      templateInstructions: (section.template_instructions === "" ? section.synopsis : section.template_instructions) || null,
      // For "Section Sources" UI
      sectionSources: sectionSources.chunks,
      sourceDataReferences: sourceDataReferences.documents,
      mrtTables: mrtTables.tables,
      // For "AI Steps" UI - Enhanced with all necessary data for display
      aiSteps: Array.isArray(section.rules)
        ? await Promise.all(section.rules.map(async (rule, ruleIdx) => {
          const ruleParams = rule.rule_parameters || {};
          let url = null;
          let chunk_content = null;
          let document_name = null;
          let section_name = null;

          // Map rule_type to appropriate data
          if (rule.rule_type === "literal_summarize_rule" ||
              rule.rule_type === "tfl_table_and_summary" ||
              rule.rule_type === "copy_paste_rule" ||
              rule.rule_tye === "copy_paste" ||
              rule.rule_type === "template_text") {
            // These rules use source_chunk_id and source_document_id
            if (ruleParams.source_chunk_id) {
              const chunk = sectionSources.chunks?.find(c => c.chunk_id === ruleParams.source_chunk_id);
              if (chunk) {
                chunk_content = chunk.content || chunk.text;
                section_name = chunk.section_name || chunk.title || `Section ${chunk.section_number || ''}`;
              }
            }
            if (ruleParams.source_document_id) {
              const doc = sourceDataReferences.documents?.find(d => d.id === ruleParams.source_document_id);
              if (doc) {
                url = doc.fileUrl || doc.url;
                document_name = doc.title || doc.fileName || doc.name;
              }
            }
          } else if (rule.rule_type === "template_text" && ruleParams.source_chunk_ids && Array.isArray(ruleParams.source_chunk_ids)) {
            // Template text with multiple source chunks (like template_table)
            const chunksData = await Promise.all(
              ruleParams.source_chunk_ids.map(async (chunkId) => {
                try {
                  // First, try to find chunk in sectionSources
                  const localChunk = sectionSources.chunks?.find(c => c.chunk_id === chunkId);
                  if (localChunk) {
                    // Try to find the document this chunk belongs to
                    const doc = sourceDataReferences.documents?.find(d => d.id === localChunk.document_id || d.id === localChunk.source_document_id);
                    return {
                      chunk_id: chunkId,
                      url: doc?.fileUrl || doc?.url || null,
                      document_name: localChunk.document_name || doc?.title || doc?.name,
                      section_name: localChunk.section_name || localChunk.title,
                      page: localChunk.page
                    };
                  }

                  // Fallback: fetch from backend if not found locally
                  const chunkData = await getChunkFromId(
                    () => {}, // setLoading
                    () => {}, // setError
                    chunkId,
                    orgAccessToken
                  );
                  return {
                    chunk_id: chunkId,
                    url: chunkData?.url,
                    document_name: chunkData?.document_name,
                    section_name: chunkData?.section_name,
                    page: chunkData?.page
                  };
                } catch (error) {
                  console.error(`Error fetching chunk ${chunkId}:`, error);
                  return null;
                }
              })
            );

            // Store pre-fetched chunk data
            document_name = "Template Text";
            section_name = `${ruleParams.source_chunk_ids.length} source${ruleParams.source_chunk_ids.length !== 1 ? 's' : ''}`;
            chunk_content = ruleParams.user_instructions || "Multiple source chunks";

            // Store chunks data in rule parameters
            ruleParams.chunks_data = chunksData.filter(c => c !== null);
          } else if (rule.rule_type === "template_table") {
            // Template table rules use source_chunk_ids array
            if (ruleParams.source_chunk_ids && Array.isArray(ruleParams.source_chunk_ids)) {
              // Pre-fetch chunk data for all source chunks
              const chunksData = await Promise.all(
                ruleParams.source_chunk_ids.map(async (chunkId) => {
                  try {
                    // First, try to find chunk in sectionSources
                    const localChunk = sectionSources.chunks?.find(c => c.chunk_id === chunkId);
                    if (localChunk) {
                      // Try to find the document this chunk belongs to
                      const doc = sourceDataReferences.documents?.find(d => d.id === localChunk.document_id || d.id === localChunk.source_document_id);
                      return {
                        chunk_id: chunkId,
                        url: doc?.fileUrl || doc?.url || null,
                        document_name: localChunk.document_name || doc?.title || doc?.name,
                        section_name: localChunk.section_name || localChunk.title,
                        page: localChunk.page
                      };
                    }

                    // Fallback: fetch from backend if not found locally
                    const chunkData = await getChunkFromId(
                      () => {}, // setLoading
                      () => {}, // setError
                      chunkId,
                      orgAccessToken
                    );
                    return {
                      chunk_id: chunkId,
                      url: chunkData?.url,
                      document_name: chunkData?.document_name,
                      section_name: chunkData?.section_name,
                      page: chunkData?.page
                    };
                  } catch (error) {
                    console.error(`Error fetching chunk ${chunkId}:`, error);
                    return null;
                  }
                })
              );

              // Store pre-fetched chunk data
              document_name = "Template Table";
              section_name = `${ruleParams.source_chunk_ids.length} source${ruleParams.source_chunk_ids.length !== 1 ? 's' : ''}`;
              chunk_content = ruleParams.user_instructions || "Multiple source chunks";

              // Store chunks data in rule parameters
              ruleParams.chunks_data = chunksData.filter(c => c !== null);
            }
          } else if (rule.rule_type === "secondary_tfl_reference") {
            // TFL references
            if (ruleParams.tfl_id) {
              const tfl = mrtTables.tables?.find(t => t.id === ruleParams.tfl_id);
              if (tfl) {
                document_name = ruleParams.referenced_table_title || `TFL ${ruleParams.tfl_id}`;
                section_name = ruleParams.section_name || tfl.title;
                url = `/tfls/${ruleParams.tfl_id}`; // TFL URL pattern
              }
            }
          } else if (rule.rule_type === "literal_content_rule") {
            // Literal content
            chunk_content = ruleParams.content || ruleParams.user_instructions;
            document_name = "Literal Content";
            section_name = ruleParams.application_context || "Content section";
          } else if (rule.rule_type === "intra_document_summarize") {
            // Intra-document summarization
            document_name = "Document Summary";
            section_name = `Sections: ${ruleParams.target_section_ids_for_summary?.join(', ') || 'Multiple sections'}`;
            chunk_content = ruleParams.summary_focus || ruleParams.synthesis_type || ruleParams.user_instructions;
          }

          return {
            rule_type: rule.rule_type,
            rule_parameters: ruleParams,
            explanation: rule.explanation,
            confidence_score: rule.confidence_score,
            url: url,
            chunk_content: chunk_content,
            document_name: document_name || `Document ${ruleIdx + 1}`,
            section_name: section_name || `Section ${ruleIdx + 1}`,
            source_chunk_id: ruleParams.source_chunk_id,
            source_document_id: ruleParams.source_document_id,
            tfl_id: ruleParams.tfl_id
          }
        }))
      : []
    }
  });
  
  // Wait for all promises to resolve and return the actual values
  return Promise.all(sectionPromises);
};


const ITEM_HEIGHT = 50;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 8.5 + ITEM_PADDING_TOP,
      width: '250px',
    }
  }
}

function getStyles(name, personName, theme) {
  return {
    fontWeight: personName && personName.includes(name) 
        ? 600
        : 400,
  };
}

// Insert Table Modal Component
function InsertTableModal({ open, onClose, tables = [], reports = [], onInsert }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTables, setSelectedTables] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [activeTab, setActiveTab] = useState("tables");

  // Ensure tables and reports are arrays
  const tablesList = Array.isArray(tables) ? tables : [];
  const reportsList = Array.isArray(reports) ? reports : [];

  // Filter tables based on search query
  const filteredTables = tablesList.filter(table => 
    table.table_title && table.table_title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter reports based on search query
  const filteredReports = reportsList.filter(report => 
    report.document_name && report.document_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Toggle table selection
  const toggleTableSelection = (table) => {
    setSelectedTables(prev => {
      const isSelected = prev.some(t => t.id === table.id);
      if (isSelected) {
        return prev.filter(t => t.id !== table.id);
      } else {
        return [...prev, table];
      }
    });
  };

  // Toggle report selection
  const toggleReportSelection = (report) => {
    setSelectedReports(prev => {
      const isSelected = prev.some(r => r.id === report.id);
      if (isSelected) {
        return prev.filter(r => r.id !== report.id);
      } else {
        return [...prev, report];
      }
    });
  };

  // Check if table is selected
  const isTableSelected = (table) => {
    return selectedTables.some(t => t.id === table.id);
  };

  // Check if report is selected
  const isReportSelected = (report) => {
    return selectedReports.some(r => r.id === report.id);
  };

  // Handle insert button click
  const handleInsert = () => {
    if (activeTab === "tables" && selectedTables.length > 0) {
      onInsert(selectedTables, "tables");
      // Reset state
      setSelectedTables([]);
      setSearchQuery("");
    } else if (activeTab === "reports" && selectedReports.length > 0) {
      onInsert(selectedReports, "reports");
      // Reset state
      setSelectedReports([]);
      setSearchQuery("");
    }
  };

  // Handle close
  const handleClose = () => {
    // Reset state
    setSelectedTables([]);
    setSelectedReports([]);
    setSearchQuery("");
    onClose();
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.5)",
        zIndex: 1300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
      onClick={handleClose}
    >
      <Box
        sx={{
          background: "#fff",
          borderRadius: 2,
          boxShadow: "0 10px 40px rgba(0,0,0,0.16)",
          width: "600px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          position: "relative"
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          pb: 0,
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          flexDirection: "column",
          color: "#1F477D"
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2
          }}>
            <Typography sx={{ fontWeight: 600, fontSize: 18, color: "#1E293B" }}>
              Insert Content
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: "#64748B" }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconButton>
          </Box>
          
          {/* Tabs */}
          <Box sx={{ display: "flex", gap: 0 }}>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                fontWeight: activeTab === "tables" ? 600 : 500,
                fontSize: 15,
                color: activeTab === "tables" ? "#1E293B" : "#64748B",
                borderBottom: activeTab === "tables" ? "2px solid #1F477D" : "2px solid transparent",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  color: "#1E293B"
                }
              }}
              onClick={() => {
                setActiveTab("tables");
                setSearchQuery("");
              }}
            >
              Tables
            </Box>
            <Box
              sx={{
                px: 3,
                py: 1.5,
                fontWeight: activeTab === "reports" ? 600 : 500,
                fontSize: 15,
                color: activeTab === "reports" ? "#1E293B" : "#64748B",
                borderBottom: activeTab === "reports" ? "2px solid #1F477D" : "2px solid transparent",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  color: "#1E293B"
                }
              }}
              onClick={() => {
                setActiveTab("reports");
                setSearchQuery("");
              }}
            >
              Reports
            </Box>
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ px: 3, pt: 2 }}>
          <TextField
            fullWidth
            placeholder={activeTab === "tables" ? "Search tables..." : "Search reports..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Box>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                fontSize: 14,
                "& fieldset": {
                  border: "none",
                },
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                },
                "&.Mui-focused": {
                  backgroundColor: "#fff",
                  border: "1px solid #3B82F6",
                }
              },
              "& .MuiInputBase-input": {
                py: 1.5,
                color: "#334155",
                "&::placeholder": {
                  color: "#94A3B8",
                  opacity: 1
                }
              }
            }}
          />
        </Box>

        {/* Content List */}
        <Box sx={{ 
          flex: 1, 
          overflow: "auto", 
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}>
          {activeTab === "tables" ? (
            // Tables Tab Content
            filteredTables.length === 0 ? (
              <Typography sx={{ 
                textAlign: "center", 
                color: "#94A3B8", 
                py: 4,
                fontSize: 14 
              }}>
                No tables found
              </Typography>
            ) : (
              filteredTables.map((table) => (
                <Box
                  key={table.id}
                  onClick={() => toggleTableSelection(table)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                    backgroundColor: isTableSelected(table) ? "#E2E8F0" : "#fff",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: isTableSelected(table) ? "#CBD5E1" : "#F8FAFC",
                      borderColor: "#CBD5E1"
                    }
                  }}
                >
                  <Box sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 1,
                    justifyContent: "space-between",
                    width: "100%"
                  }}>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      fontSize: 15, 
                      color: "#1E293B",
                      mb: 0.5
                    }}>
                      {table.table_title}
                    </Typography>
                    <Chip 
                      label={table.table_location_type.toUpperCase()} 
                      sx={{
                        backgroundColor: table.table_location_type.toLowerCase() === "in-text" ? "#1F477D" : "#1F477D",
                        color: table.table_location_type.toLowerCase() === "in-text" ? "#fff" : "#fff",
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: 1,
                        ml: 1,
                        mt: 0.5
                      }} 
                    />
                  </Box>
                  <Typography sx={{ 
                    fontSize: 14, 
                    color: "#64748B",
                    lineHeight: 1.5
                  }}>
                    {table.table_description}
                  </Typography>
                </Box>
              ))
            )
          ) : (
            // Reports Tab Content
            filteredReports.length === 0 ? (
              <Typography sx={{ 
                textAlign: "center", 
                color: "#94A3B8", 
                py: 4,
                fontSize: 14 
              }}>
                {reportsList.length === 0 ? "No reports available" : "No reports found"}
              </Typography>
            ) : (
              filteredReports.map((report) => (
                <Box
                  key={report.id}
                  onClick={() => toggleReportSelection(report)}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                    backgroundColor: isReportSelected(report) ? "#E2E8F0" : "#fff",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: isReportSelected(report) ? "#CBD5E1" : "#F8FAFC",
                      borderColor: "#CBD5E1"
                    }
                  }}
                >
                  <Typography sx={{ 
                    fontWeight: 600, 
                    fontSize: 15, 
                    color: "#1E293B",
                    mb: 0.5
                  }}>
                    {report.document_name}
                  </Typography>
                  <Typography sx={{ 
                    fontSize: 14, 
                    color: "#64748B",
                    lineHeight: 1.5
                  }}>
                    {extractDocumentSynopsis(report.document_synopsis)}
                  </Typography>
                </Box>
              ))
            )
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: 3, 
          borderTop: "1px solid #E5E7EB",
          display: "flex",
          gap: 2,
          justifyContent: "flex-end"
        }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: 1.5,
              borderColor: "#E5E7EB",
              color: "#475569",
              fontWeight: 500,
              fontSize: 14,
              px: 3,
              py: 1,
              textTransform: "none",
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F8FAFC"
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInsert}
            disabled={activeTab === "tables" ? selectedTables.length === 0 : selectedReports.length === 0}
            sx={{
              borderRadius: 1.5,
              backgroundColor: "#3B82F6",
              fontWeight: 500,
              fontSize: 14,
              px: 3,
              py: 1,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#2563EB",
                boxShadow: "none"
              },
              "&:disabled": {
                backgroundColor: "#E2E8F0",
                color: "#94A3B8"
              }
            }}
          >
            {activeTab === "tables" ? "Insert Table" : "Insert Report"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function SectionTabComponent({
  id, 
  setIsRegenerating,
  onSourceCardClick,
  onOpenTemplateButtonClick,
  templateId,
  selectedText, 
  setSelectedText,
  selectedSectionIdx, 
  setSelectedSectionIdx,
  setError,
  setLoading,
  loading,
  setErrorMessage,
  editorInstance,
  mrtJson,
  sections,
  setSections,
  documentSources,
  setDocumentSources,
  sourceDataReferences,
  setSourceDataReferences,
  mrtTables,
  setMRTTables,
  expanded,
  handleAccordionChange,
  combinedDocumentSources,
  setCombinedDocumentSources,
  handleOptionSelect,
  setSuccess,
  sourceDocuments,
  onFileSelect,
  dateUploaded,
  templateNickname,
  documentMRTTables,
  connectorId,
  navigate,
  documentSet,
  selectedSources
}) {
  const theme = useTheme();
  // Tab state: "sections" or "document"

  const [activeTab, setActiveTab] = useState("details");
  // Edit Section UI states
  const [editSectionTab, setEditSectionTab] = useState("content");
  const [lengthOption, setLengthOption] = useState("shorter");
  const [tenseOption, setTenseOption] = useState("present");
  // Section dropdown state is now managed by parent
  const [regenerateText, setRegenerateText] = useState("");
  const [addContentModalOpen, setAddContentModalOpen] = useState(false);
  const [updateSourceDocumentModalOpen, setUpdateSourceDocumentModalOpen] = useState(null);
  const [currentReviewingSourceDocument, setCurrentReviewingSourceDocument] = useState(null);

  // Helper for quick action buttons
  const regenerateSelectionWithPrompt = async (prompt) => {
    await regenerateSelection(prompt);
  };
  /* Section sources are now loaded from section data, not local state */
  const [tableHtml, setTableHtml] = useState([]);

  const [mrtTablesList, setMRTTablesList] = useState([]);
  const [mrtReportsList, setMRTReportsList] = useState([]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sectionSelectionModalOpen, setSectionSelectionModalOpen] = useState(false);
  const [currentReviewingSourceDocumentType, setCurrentReviewingSourceDocumentType] = useState(null);

  // Document Description States
  const [documentName, setDocumentName] = useState('')
  const [productName, setProductName] = useState('')
  const [templateType, setTemplateType] = useState({})

  const [outlineSections, setOutlineSections] = useState([])
  const [inputFiles, setInputFiles] = useState([]);
  const [sourceModalOpen, setSourceModalOpen] = useState(false)
  const [selectedCloudFileNames, setSelectedCloudFileNames] = useState([])
  const [selectedCloudFiles, setSelectedCloudFiles] = useState([])
  const [multipleFileNameList, setMultipleFileNameList] = useState([])
  const [multipleFileList, setMultipleFileList] = useState([])
  const [sectionFormat, setSectionFormat] = useState(0)
  // Removed expandedCards state - no longer needed for dropdown

  const handleFormatChange = (_, newValue) => {
    setSectionFormat(newValue)
  }

  // Removed toggleCardExpansion - no longer needed for dropdown


  useEffect(() => {
    console.log('selectedCloudFiles', selectedCloudFiles)
    console.log('selectedCloudFileNames', selectedCloudFileNames)
    console.log('multipleFileList', multipleFileList)
    console.log('multipleFileNameList', multipleFileNameList)
  }, [selectedCloudFiles, selectedCloudFileNames, multipleFileList, multipleFileNameList])

  
  // Global source document status management
  const {
    getStatus: getSourceDocumentStatus,
    setStatus: setSourceDocumentStatus, 
    startIngestion: startSourceDocumentIngestion,
    isPolling: isSourceDocumentPolling,
    cancelPolling,
    cancelAllPolling: cancelAllSourceDocumentPolling,
    getActivePollingDocuments: getActivePollingSourceDocuments,
    forceStopPollingByTaskId: forceStopSourceDocumentPollingByTaskId,
    getPollingDebugInfo: getSourceDocumentPollingDebugInfo,
    setDocumentFilePath,
    getDocumentFilePath,
    removeDocumentFilePath,
    findDocumentByFilePath,
    SourceDocumentStatus
  } = useSourceDocumentStatus()

  async function getOrgAccessToken(orgId) {
    console.log(orgId + ' is orgId')
    const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)
    return orgAccessToken
  }
  

  const handleFileUpload = async (files, fileContents) => {
    try {
      const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);
      const currentFolder = "input";

      await uploadMultipleFilesUpdated(
        setLoading,
        files.map((file, index) => ({
          name: file.name,
          content: fileContents[index],
        })),
        fileContents,
        (error) => {
          console.error('Upload error:', error)
        },
        currentFolder,
        orgAccessToken
      )
    } catch (error) {
      console.error('Upload failed:', error)
    } finally {
      setLoading(false)
    }
  }


  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    const fileContents = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = (e) => resolve(e.target.result)
            reader.readAsArrayBuffer(file)
          })
      )
    )

    const fileName = files[0].name
    const filePath = "input/" + fileName
    console.log('filePath', filePath)
    console.log('connectorId', connectorId)

    // Set specific document status to uploading
    const documentId = updateSourceDocumentModalOpen || `new-document-${Date.now()}`

    const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgs[0].orgId)

    try {
      console.log('orgAccessToken', orgAccessToken)

      // Upload file first
      await handleFileUpload(files, fileContents)

      // Create file path relationship after successful upload
      setDocumentFilePath(documentId, filePath, connectorId)

      // Use global polling system for source document ingestion
      const taskId = await startSourceDocumentIngestion({
        documentId,
        filePaths: [filePath],
        connectorId,
        orgAccessToken,
        onProgress: (progress, message) => {
          console.log(`Document ${documentId} progress: ${progress}% - ${message}`)
        },
        onSuccess: (result) => {
          console.log(`Document ${documentId} ingestion completed successfully:`, result)
          // File path relationship persists on successful ingestion
        },
        onError: (error) => {
          console.error(`Document ${documentId} ingestion failed:`, error)
          // File path relationship will be automatically removed by SourceDocumentStatusManager
        }
      })
      
      console.log(`Started global polling for document ${documentId}, task ${taskId}`)
      
    } catch (error) {
      console.error('Upload/ingestion failed:', error)
      // Reset status on error using global state
      setSourceDocumentStatus(documentId, SourceDocumentStatus.WAITING)
      // Remove relationship on upload failure
      removeDocumentFilePath(documentId)
    } finally {
      e.target.value = ''
      setAddDocSourceModalOpen(false)
      setUpdateSourceDocumentModalOpen(null)
    }
  }

  const handleCloudFileSelect = async () => {
    const filePath = selectedCloudFiles[0].name.split('/').slice(1).join('/')
    console.log('filePath', filePath)
    const documentId = updateSourceDocumentModalOpen || `new-document-${Date.now()}`

    try {
      const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org)
      console.log('orgAccessToken', orgAccessToken)

      // Create file path relationship after successful upload
      setDocumentFilePath(documentId, filePath, connectorId)

      // Use global polling system for source document ingestion
      const taskId = await startSourceDocumentIngestion({
        documentId,
        filePaths: [filePath],
        connectorId,
        orgAccessToken,
        onProgress: (progress, message) => {
          console.log(`Document ${documentId} progress: ${progress}% - ${message}`)
        },
        onSuccess: (result) => {
          console.log(`Document ${documentId} ingestion completed successfully:`, result)
        },
        onError: (error) => {
          console.error(`Document ${documentId} ingestion failed:`, error)
        }
      })

      console.log(`Started global polling for document ${documentId}, task ${taskId}`)

    } catch (error) {
      console.error('Error selecting cloud file:', error)
      setSourceDocumentStatus(documentId, SourceDocumentStatus.WAITING)
      removeDocumentFilePath(documentId)
    } finally {
      setAddDocSourceModalOpen(false)
      setUpdateSourceDocumentModalOpen(null)
    }
  }

  const handleUpdateSourceDocumentLocal = (documentId) => {
    setUpdateSourceDocumentModalOpen(documentId)
  }

  const authInfo = useAuthInfo();
  const org = authInfo.orgHelper.getOrgs()[0].orgId;
  const orgs = authInfo.orgHelper.getOrgs();

  // Helper functions for managing source document statuses locally
  const getSourceDocumentStatusLocal = (documentId) => {
    // Use global source document status (persistent across page navigation)
    const globalStatus = getSourceDocumentStatus(documentId)
    console.log('globalStatus', globalStatus)
    
    // Check if source document is being polled globally
    const isPolling = isSourceDocumentPolling(documentId)
    if (isPolling) {
      console.log(`Source Document ${documentId} status: ${globalStatus} (globally polling)`)
    }
    
    return globalStatus
  }

  const setSourceDocumentStatusLocal = (documentId, status) => {
    // Map legacy status names to SourceDocumentStatus enum
    const statusMap = {
      'waiting': SourceDocumentStatus.WAITING,
      'uploading': SourceDocumentStatus.UPLOADING,
      'review': SourceDocumentStatus.REVIEW
    }
    
    const mappedStatus = statusMap[status] || status
    setSourceDocumentStatus(documentId, mappedStatus)
  }

  // DEV: Function to reset document state to waiting
  const handleResetToWaiting = (documentId) => {
    if (process.env.NODE_ENV === 'development' && documentId) {
      console.log(`[DEV] Resetting document ${documentId} to waiting state`)
      // Cancel any active polling
      cancelPolling(documentId)
      // Reset status to waiting
      setSourceDocumentStatusLocal(documentId, 'waiting')
    }
  }

  /**
   * Handles forcing a document into review state
   * @param {string} documentId - The document ID to force into review
   */
  const handleForceReview = (documentId) => {
    if (documentId) {
      console.log(`Forcing document ${documentId} into review state`);
      // Cancel any active polling
      cancelPolling(documentId);
      // Set status to review
      setSourceDocumentStatusLocal(documentId, 'review');
    }
  };

  /**
   * Handles source card click based on document type and routes to appropriate modal
   * @param {Object} sourceDocument - The source document object
   */
  const handleSourceCardReviewClick = (sourceDocument) => {
    // Find the document type from sourceDocuments array
    const documentWithType = sourceDocuments.find(
      doc => doc.document_id === sourceDocument.document_id
    );
    
    const documentType = documentWithType?.document_type || null;
    console.log('Document clicked for review:', sourceDocument.document_name, 'Type:', documentType);
    
    // Store the document type for later use
    setCurrentReviewingSourceDocumentType(documentType);
    
    // Determine which modal to open based on document type
    console.log('documentType', documentType)
    if (documentType === 'TFL') {
      // Open AssignTablesModal for TFL documents
      setCurrentReviewingSourceDocument(sourceDocument);
      setSectionSelectionModalOpen(false);
    } else {
      // Open SectionSelectionModal for source documents
      setCurrentReviewingSourceDocument(sourceDocument);
      setSectionSelectionModalOpen(true);
    }
  };

  /**
   * Handles closing both modals and resetting states
   */
  const handleCloseModals = () => {
    setCurrentReviewingSourceDocument(null);
    setSectionSelectionModalOpen(false);
    setCurrentReviewingSourceDocumentType(null);
  };

  const handleConfirmSectionsToRegenerate = async (sourceDocument, selectedSectionIds) => {
    const documentId = sourceDocument?.document_id || ""
    const filePathObject = getDocumentFilePath(documentId)

    if (!documentId || !filePathObject) {
      console.error('No source document found for regeneration');
      return
    }

    const filePath = filePathObject.filePath
    
    try {
      const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);
      // Use section titles as identifiers since sections don't have explicit section_id
      const sectionIds = selectedSectionIds || [];
      
      let document_source_path = ""

      if (filePath.includes(org)) {
        document_source_path = filePath
      } else {
        document_source_path = `${org}/${filePath}`
      }

      const connector_data_id = filePath.connector_data_id || mrtJson.connector_data_ids[0]
      
      const payload = {
        document_id: id,
        regeneration_type: "source_update",
        update_config: {
          source_updates: [ 
            {
              new_source_path: document_source_path,
              doc_set_key: connector_data_id,
              section_ids: sectionIds
            }
          ]
        }
      };
      
      console.log('Section regeneration payload:', payload);
      
      const result = await documentsRegenerateV1(payload, orgAccessToken);
      
      console.log('documentsRegenerateV1 result:', result);
      
      // Check if we got a successful response (202 or 200)
      if (result && (result.status === 202 || result.status === 200)) {
        // Clear localStorage for section selections on successful regeneration
        if (documentId && templateNickname) {
          console.log('Clearing section selection from localStorage after successful regeneration');
          sectionSelectionStorage.clearSelections(documentId, templateNickname);
          sectionSelectionStorage.clearSectionData(documentId, templateNickname);
        }
      }
      
      // Success! Now perform cleanup actions:
      
      // 1. Reset document status to "waiting"
      if (documentId) {
        setSourceDocumentStatusLocal(documentId, "waiting");
        console.log(`Source Document ${documentId} status reset to waiting after successful section regeneration`);
      }
      
      // 2. Close the modal
      handleCloseModals();
      
      // 3. Navigate back to document set workspace
      if (navigate && documentSet) {
        // Store success message in sessionStorage for showing a toast after navigation
        sessionStorage.setItem('regenerationSuccess', JSON.stringify({
          message: 'Document regeneration started successfully. You will receive an email when it\'s ready.',
          documentId: id,
          timestamp: Date.now()
        }));
        
        // Navigate to the document set workspace
        navigate(`/documentSet/${documentSet}`);
      } else if (navigate) {
        // Fallback to home if we don't have the document set
        console.warn('Document set not found, navigating to home');
        sessionStorage.setItem('regenerationSuccess', JSON.stringify({
          message: 'Document regeneration started successfully. You will receive an email when it\'s ready.',
          documentId: id,
          timestamp: Date.now()
        }));
        navigate('/');
      }
      
      // return result
    } catch (error) {
      console.error('Failed to regenerate sections:', error);
      // Re-throw the error so the modal can handle it
      throw error;
    }
  }

  const handleSourceModalClose = () => {
    setSourceModalOpen(false)
    handleCloudFileSelect()
  }

  const handleSourceSelect = (selectedFiles) => {
    console.log('selectedFiles', selectedFiles)
    // Store full paths in selectedCloudFiles
    setSelectedCloudFiles(
      selectedFiles.map((file) => ({
        name: file.name,
        url: file.url,
        id: file.id,
        isCloud: true,
      }))
    )

    // Store clean filenames using the same cleaning method as handleGenerateDocument
    const cleanFileNames = selectedFiles.map((file) => {
      const index = file.name.indexOf('/')
      return index !== -1 ? file.name.substring(index + 1) : file.name
    })
    setSelectedCloudFileNames(cleanFileNames)

    // Update the file name list to match exactly what's selected
    setMultipleFileNameList((prevList) => {
      // Remove all cloud files first
      const localFiles = prevList.filter((name) =>
        multipleFileList.some((file) => file.name === name)
      )
      // Add all currently selected cloud files
      return [...localFiles, ...cleanFileNames]
    })
  }

  const handleInsertAllCompleteLocal = async (sourceDocument, tableMappings) => {
    try {
      // Get the access token
      const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org)

      // Build the regeneration requests from table mappings
      const regenerationRequests = tableMappings.map(mapping => ({
        regeneration_type: "tfl_update",
        connector_updates: {
          old_connector_data_id: [
            mapping.templateTableId
          ],
          new_connector_data_id: mapping.replacementTableId
        }
      }))

      // Create the API payload
      const payload = {
        document_id: id,
        regeneration_requests: regenerationRequests
      }

      console.log('Regeneration payload:', payload)

      // Call the regeneration API
      const result = await documentsRegenerateV1(payload, orgAccessToken)
      console.log('documentsRegenerateV1 result:', result)
      
      // Success! Now perform cleanup actions:
      
      // 1. Reset document status to "waiting" (or remove from storage)
      if (sourceDocument?.document_id) {
        // Option 1: Set to "waiting" status
        setSourceDocumentStatusLocal(sourceDocument.document_id, "waiting")
        
        // Option 2: Remove from status tracking entirely to prevent overflow
        // Alternative: removeDocumentFilePath(sourceDocument.document_id)
        // This would implicitly set it to "waiting" since null/undefined = waiting
        
        console.log(`Source Document ${sourceDocument.document_id} status reset to waiting after successful Insert All`)
      }
      
      // 2. Close the modal
      setCurrentReviewingSourceDocument(null)
      
      // 3. Navigate back to document set workspace
      if (navigate && documentSet) {
        // Store success message in sessionStorage for showing a toast after navigation
        sessionStorage.setItem('regenerationSuccess', JSON.stringify({
          message: 'Document regeneration started successfully. You will receive an email when it\'s ready.',
          documentId: id,
          timestamp: Date.now()
        }))
        
        // Navigate to the document set workspace
        navigate(`/documentSet/${documentSet}`)
      } else if (navigate) {
        // Fallback to home if we don't have the document set
        console.warn('Document set not found, navigating to home')
        sessionStorage.setItem('regenerationSuccess', JSON.stringify({
          message: 'Document regeneration started successfully. You will receive an email when it\'s ready.',
          documentId: id,
          timestamp: Date.now()
        }))
        navigate('/')
      }
      
      // Return success to the modal
      return result
    } catch (error) {
      console.error('Failed to regenerate document:', error)
      // Re-throw the error so the modal can handle it
      throw error
    }
  }

  const handleCancelAllSourceDocumentPollingLocal = () => {
    const cancelledCount = cancelAllSourceDocumentPolling()
    console.log(`Cancelled ${cancelledCount} active source document polling tasks`)
    
    // Optionally show a success message or notification
    if (cancelledCount > 0) {
      // You could add a toast notification here if available
      console.log(`Successfully cancelled ${cancelledCount} source document polling tasks and reset statuses to waiting`)
    } else {
      console.log('No active source document polling tasks to cancel')
    }
  }

  const handleDebugSourceDocumentPollingLocal = () => {
    const debugInfo = getSourceDocumentPollingDebugInfo()
    console.log('=== SOURCE DOCUMENT POLLING DEBUG INFO ===')
    console.log('Source Document Manager Active Polls:', debugInfo.documentManager.activePolls)
    console.log('Source Document Manager Polls:', debugInfo.documentManager.polls)
    console.log('Task Poller Active Count:', debugInfo.taskPoller.activeCount)
    console.log('Task Poller Details:', debugInfo.taskPoller.details)
    console.log('Mismatches - Orphaned Tasks:', debugInfo.mismatch.orphanedTasks)
    console.log('Mismatches - Unmapped Source Documents:', debugInfo.mismatch.unmappedDocuments)
    console.log('===============================================')
    
    // Log a summary for easy reading
    const summary = {
      sourceDocumentManagerPolls: debugInfo.documentManager.activePolls,
      taskPollerPolls: debugInfo.taskPoller.activeCount,
      orphanedTasks: debugInfo.mismatch.orphanedTasks.length,
      unmappedSourceDocuments: debugInfo.mismatch.unmappedDocuments.length
    }
    console.log('SOURCE DOCUMENT POLLING SUMMARY:', summary)
    
    return debugInfo
  }


  
  useEffect(() => {
    // console.log('editorInstance', editorInstance)
    // console.log('dateUploaded', dateUploaded)
    // console.log("type of dateUploaded", typeof dateUploaded)
    let tableHtmlArray = [];

    if (editorInstance) {
      let tableHTMLConnector = editorInstance.createConnector()
      tableHTMLConnector.executeMethod('GetFileHTML', null, function(res) {
        const totalTableHtml = res.match(/<table[\s\S]*?<\/table>/gi) || []
        const exampleNumber = totalTableHtml.length > 3 ? 3 : totalTableHtml.length

        tableHtmlArray = totalTableHtml.slice(0, exampleNumber)

        setTableHtml(tableHtmlArray)
      })
      // console.log('tableHtmlArray', tableHtmlArray)
    }
  }, [editorInstance])

  
  useEffect(() => {
    async function getMRTTablesList() {
      // Check if mrtJson and connector_data_ids exist before proceeding
      if (!mrtJson || !mrtJson.connector_data_ids) {
        console.log('Waiting for mrtJson to load...');
        return;
      }
      
      const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);
      const response = await getAllMRTTables(mrtJson.connector_data_ids, orgAccessToken);
      // Handle the response - it might be an object with a 'tables' property
      const tablesList = response?.tables || response || [];
      setMRTTablesList(Array.isArray(tablesList) ? tablesList : []);
    }
    getMRTTablesList();
  }, [mrtJson]);

  useEffect(() => {
    async function getMRTReportsList() {
      if (!mrtJson || !mrtJson.connector_data_ids) {
        console.log('Waiting for mrtJson to load...');
        return;
      }

      console.log('mrtJson', mrtJson)
      console.log('mrtJson.connector_data_ids', mrtJson.connector_data_ids)
      
      const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);
      const response = await getAllMRTReports(mrtJson.connector_data_ids, orgAccessToken);
      console.log('response', response)
      console.log('response.documents', response.documents)
      const reportsList = response?.documents || response || [];
      console.log('reportsList', reportsList)
      setMRTReportsList(Array.isArray(reportsList) ? reportsList : []);
    }
    getMRTReportsList();
  }, [mrtJson]);

  const regenerateSelection = async (
    prompt
  ) => {
    setIsRegenerating(true)
    // Ensure mrtJson is loaded before proceeding
    if (!mrtJson) {
        console.error('Cannot regenerate selection: mrtJson not loaded');
        setError(true);
        return;
    }

    setLoading(true);

    try {
        const org = authInfo.orgHelper.getOrgs()[0].orgId;
        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);

        // console.log('selectedText', selectedText)
        // console.log("regenerateText", regenerateText)
        
        // Get the table HTML from the document
        let connector = null;
        let tableHtmlArray = [];
        const mrtDocumentIndex = 'mrt_source_document_index'
        const connectorDataIds = mrtJson?.connector_data_ids || []

        // Get the OnlyOffice connector
        connector = editorInstance.createConnector()
        // console.log('editorInstance', editorInstance)
        // console.log('connector', connector)


        tableHtmlArray = tableHtml
        // console.log('tableHtmlArray', tableHtmlArray)

        // TODO: Add the source index to the regenerateSelectionMRT call. These are hardcoded for now.
        const result = await regenerateSelectionMRT(
          id, 
          connectorDataIds, 
          mrtDocumentIndex, 
          selectedText, 
          prompt ? prompt : regenerateText, 
          "output",
          orgAccessToken, 
          tableHtmlArray
        )

        // const searchProperties = {
        //     oProperties: {
        //         searchString: selectedText,
        //         matchCase: false,
        //         isForward: true
        //     },
        //     searchString: selectedText,
        //     matchCase: false,
        //     isForward: true
        // }

        // console.log('result', result)

        // connector.executeMethod('SearchNext', [searchProperties], (match) => {
        //     console.log('match', match)
        // })
        
        // Remove selected content first
        connector.executeMethod(
            'RemoveSelectedContent',
            [],
            (result) => {
                console.log('Remove content result:', result)
            }
        )
        
        // Insert the regenerated content with formatting preservation
        await insertMixedContentWithFormatting(connector, result.regenerated_content)
        setRegenerateText("")
        setSelectedText("")
        setIsRegenerating(false)
        setSuccess(true)
    } catch (error) {
        console.error('Error regenerating selection:', error)
        setErrorMessage('Error while regenerating selection. Add custom instructions and try again.')
        setError(true)
        setSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  const [addDocSourceModalOpen, setAddDocSourceModalOpen] = useState(false);


  // Delete handler for source document cards
  const handleDeleteSourceDocumentLocal = (document_id) => {
    setCombinedDocumentSources(prev => prev.filter((item) => item.document_id !== document_id));
  };
  
  const handleDeleteMRTTable = (idx) => {
    setMRTTables(prev => prev.filter((_, i) => i !== idx));
  };

  // Get Array of all section sources and their page numbers
  useEffect(() => {
    const fetchDocumentSources = () => {
      if (!sections || sections.length === 0) return;
      
      if (sourceDocuments.length > 0) {
        setCombinedDocumentSources(sourceDocuments)
      } else {
        const docSourcesArray = sections.map(section => section.sectionSources).flat()
        const sourceDataReferenceArray = sections.map(section => section.sourceDataReferences).flat()
        const mrtTableArray = sections.map(section => section.mrtTables).flat()

        const nonRepeatedDocuments = [...new Set(docSourcesArray.map(doc => doc.document_id))].map(docId => {
          return docSourcesArray.find(doc => doc.document_id === docId)
        })

        const nonRepeatedSourceDataReferences = [...new Set(sourceDataReferenceArray.map(doc => doc.document_id))].map(docId => {
          return sourceDataReferenceArray.find(doc => doc.document_id === docId)
        })

        const nonRepeatedMRTTables = [...new Set(mrtTableArray.map(doc => doc.file_path))].map(filePath => {
          return mrtTableArray.find(doc => doc.file_path === filePath)
        }).filter(doc => doc).map(doc => {
          return {
            document_id: doc.id,
            document_name: doc.file_path.split('/').pop(),
            url: doc.url
          }
        })

        setMRTTables(nonRepeatedMRTTables)

        const validDocuments = nonRepeatedDocuments.filter(doc => doc && doc.document_name);
        const validSourceDataReferences = nonRepeatedSourceDataReferences.filter(doc => doc && doc.document_name);
        
        const allDocNames = [...new Set([
          ...validDocuments.map(doc => doc.document_name),
          ...validSourceDataReferences.map(doc => doc.document_name)
        ])];
        
        const calculatedCombinedDocumentSources = allDocNames.map(docName => {
          const foundDoc = validDocuments.find(doc => doc.document_name === docName) ||
                          validSourceDataReferences.find(doc => doc.document_name === docName);
          return foundDoc ? {
            document_id: foundDoc.document_id,
            document_name: docName,
            url: foundDoc.url || foundDoc.presigned_url || null,
            page: foundDoc.page || 1,
            section_name: foundDoc.section_name || docName
          } : null;
        }).filter(Boolean);

        setCombinedDocumentSources(calculatedCombinedDocumentSources)
      }
    }
    
    fetchDocumentSources()
  }, [sections]);

  const onSourceCardClickSourceTab = async (docStatus, item) => {
    console.log('docStatus', docStatus)
    console.log('item', item)
    if (docStatus === "review") {
      handleSourceCardReviewClick(item)
    } else if (docStatus === "uploading") {
      console.log('uploading')
    } else if (onSourceCardClick) {
      try {
        // Generate presigned URL if not available
        let fileUrl = item.url;

        if (!fileUrl && item.document_id) {
          // Try to generate a presigned URL for the document
          const orgAccessToken = authInfo.tokens.getAccessTokenForOrg(org);
          const presignedUrlResponse = await generatePresignedUrl(
            item.document_name,
            orgAccessToken,
            org
          );

          if (presignedUrlResponse && presignedUrlResponse.url) {
            fileUrl = presignedUrlResponse.url;
          }
        }

        if (fileUrl) {
          onSourceCardClick({
            fileUrl: fileUrl,
            page: item.page || 1,
            title: item.section_name || item.document_name,
            templateURL: fileUrl
          })
        } else {
          console.error('Unable to get document URL for:', item.document_name)
          setError('Unable to open document. Please try again.')
        }
      } catch (error) {
        console.error('Error opening source document:', error)
        setError('Error opening document. Please try again.')
      }
    } else {
      console.log('onSourceCardClick function not provided')
      return undefined
    }
  }


  // Auto-select first section if none selected but sections are available
  const effectiveSelectedIdx = (selectedSectionIdx === -1 || selectedSectionIdx === undefined) && sections.length > 0
    ? 0
    : selectedSectionIdx;

  const selectedSection = (effectiveSelectedIdx >= 0 && effectiveSelectedIdx < sections.length)
    ? sections[effectiveSelectedIdx]
    : null;

  // Auto-select first section if none selected and sections are available
  // Commented out to prevent infinite loop - can be re-enabled if needed
  // useEffect(() => {
  //   if ((selectedSectionIdx === -1 || selectedSectionIdx === undefined) && sections.length > 0 && setSelectedSectionIdx) {
  //     setSelectedSectionIdx(0)
  //   }
  // }, [selectedSectionIdx, sections, setSelectedSectionIdx])

  // Use aiSteps which already have all necessary data
  const displayItems = selectedSection?.aiSteps || selectedSection?.rules || []

  const cleanTemplateInstructions = (templateInstructions) => {
    console.log('templateInstructions', templateInstructions)
    if (templateInstructions) {
      return templateInstructions.replace(/\[[A-Z-]+\]/g, '')
    }
    return templateInstructions
  }

  return (
    <Box
      sx={{
        background: "#fff",
        height: "100%",
        width: "100%",
        px: 0,
        py: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Details/Edit Tabs - Fixed at very top */}
      <Box
        sx={{
          display: "flex",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
          px: 0,
          pt: 0,
          mb: 0,
          height: "48px",
          minHeight: "48px",
          maxHeight: "48px",
          flexShrink: 0, // Prevent shrinking
        }}
      >
        <Box
          sx={{
            flex: 1,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            fontWeight: activeTab === "details" ? 600 : 500,
            fontSize: 16,
            color: activeTab === "details" ? "#1E293B" : "#64748B",
            borderBottom: activeTab === "details" ? "2px solid #1F477D" : "2px solid transparent",
            background: "#fff",
            cursor: "pointer",
            letterSpacing: 0.1,
            transition: "border-bottom 0.15s, color 0.15s",
          }}
          onClick={() => setActiveTab("details")}
        >
          Details
        </Box>
        <Box
          sx={{
            flex: 1,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            fontWeight: activeTab === "edit" ? 600 : 500,
            fontSize: 16,
            color: activeTab === "edit" ? "#1E293B" : "#64748B",
            borderBottom: activeTab === "edit" ? "2px solid #1F477D" : "2px solid transparent",
            background: "#fff",
            cursor: "pointer",
            letterSpacing: 0.1,
            transition: "border-bottom 0.15s, color 0.15s",
          }}
          onClick={() => setActiveTab("edit")}
        >
          Edit
        </Box>
      </Box>
      {activeTab === "details" && (
        <Box sx={{
          flex: 1, // Fill remaining height after tabs
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevent overflow on this container
          background: "#fff",
          minHeight: 0, // Important for flex children
        }}>
          {/* Fixed header container for Section dropdown AND Sources - ALWAYS VISIBLE */}
          <Box
            sx={{
              background: "#fff",
              borderTop: "1px solid #E5E7EB",
              flexShrink: 0, // Don't shrink - always visible
            }}
          >
            {/* Section Dropdown */}
            <Box sx={{ px: 2, pt: 2, pb: 2 }}>
              <Select
                value={effectiveSelectedIdx}
                onChange={e => {
                  setSelectedSectionIdx(e.target.value)
                  const selectedSection = sections[e.target.value]
                  if (selectedSection) {
                    handleOptionSelect(selectedSection)
                  }
                }}
                displayEmpty
                fullWidth
                MenuProps={MenuProps}
                size="medium"
                sx={{
                  background: "#fff",
                  borderRadius: 2,
                  fontWeight: 500,
                  fontSize: 14,
                  height: 40,
                  boxShadow: "none",
                  border: "1px solid #E5E7EB",
                  "& .MuiSelect-select": {
                    py: 1,
                    px: 2,
                    color: "#475569",
                  },
                  "& fieldset": { border: "none" },
                }}
                renderValue={selected => {
                  if (selected === -1 || selected === "" || selected === undefined) {
                    return <span style={{ color: "#94A3B8" }}>Select a section</span>;
                  }
                  return sections[selected]?.title || "Select a section";
                }}
              >
                <MenuItem value={-1} disabled>
                  Select a section
                </MenuItem>
                {sections.map((section, idx) => (
                  <MenuItem
                    key={idx}
                    value={idx}
                    style={getStyles(section.title, selectedSection.title, theme)}
                  >
                    {section.title}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {/* Sources text - FIXED below Section dropdown */}
            <Box sx={{
              px: 2,
              pb: 2,
              borderBottom: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center"
            }}>
              <Typography sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#1E293B",
              }}>
                Sources · {displayItems?.length || 0}
              </Typography>
              <Tooltip
                title="Sources are the most relevant documents that were used to generate the section"
                placement="right"
                arrow
              >
                <Box sx={{ ml: 1, display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <svg width="16" height="16" fill="none" xmlns="https://www.w3.org/2000/svg">
                    <circle cx="8" cy="8" r="8" fill="#E2E8F0"/>
                    <text x="8" y="11.5" textAnchor="middle" fontSize="10" fill="#64748B" fontFamily="Arial">?</text>
                  </svg>
                </Box>
              </Tooltip>
            </Box>
          </Box>
          {/* ONLY Scrollable container - EXCLUSIVELY for rule cards */}
          <Box sx={{
            flex: 1, // Take remaining space between header and footer
            overflowY: "auto", // ONLY this container scrolls
            overflowX: "hidden",
            background: "#F8FAFC",
            px: 2,
            py: 2,
            minHeight: 0, // Important for proper flex scrolling
          }}>
            {/* Rule Cards container */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Display cards for each rule */}
              {/* Use displayItems (aiSteps or rules) for rendering cards */}
              {displayItems && displayItems.length > 0 ? (
                console.log('displayItems', displayItems),
                displayItems.map((item, idx) => {
                  // Adapt sourceDataReferences to rule-like structure if needed
                  const rule = item.rule_type ? item : {
                    rule_type: "copy_paste_rule", // Default rule type for legacy sourceDataReferences
                    rule_parameters: {
                      source_chunk_id: item.chunkId || item.chunk_id,
                      source_document_id: item.documentId || item.document_id || item.id,
                    },
                    ...item
                  };
                  const ruleParams = rule.rule_parameters || item.params || {};
                  // Don't use source_data_references anymore - use rule_parameters instead

                  let documentName = item.documentName;    
                  let page = item.page;

                  console.log('[SectionTabComponent] documentName', documentName)
                  console.log('[SectionTabComponent] page', page)

                  // Format rule type for display
                  const formatRuleType = (type) => {
                    if (!type) return "Unknown tool";
                    return type.split('_').map(word =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    ).join(' ');
                  };

                  // Determine the action label text based on rule type
                  const getActionLabel = (ruleType) => {
                    switch(ruleType) {
                      case "copy_paste_rule" || "copy_paste":
                        return "Copy/Paste";
                      case "literal_summarize_rule":
                        return "Summarize from Source";
                      case "tfl_table_and_summary":
                        return "Table Summary";
                      case "template_table":
                        return "Template Table";
                      case "secondary_tfl_reference":
                        return "Secondary TFL Reference";
                      case "intra_document_summarize":
                        return "Intra-document Summarize";
                      case "literal_content_rule":
                        return "Use Boilerplate Text";
                      case "placeholder_rule":
                        return "Placeholder Text";
                      case "template_text_rule":
                        return "Use Template Text";
                      case "template_text":
                        return "Use Template Text";
                      case "fill_in_blank":
                        return "Fill in the Blank";
                      case "fill_in_the_blank":
                        return "Fill in the Blank";
                      default:
                        return formatRuleType(ruleType);
                    }
                  };

                  // Determine if the card should be clickable based on rule type
                  const isClickable = (ruleType) => {
                    return ruleType !== "intra_document_summarize" &&
                           ruleType !== "literal_content_rule" &&
                           ruleType !== "placeholder_rule";
                  };

                  // Determine if this specific card instance is clickable
                  const isCardClickable = () => {
                    if (!isClickable(rule.rule_type)) return false;
                    // template_table and template_text with source_chunk_ids use chips instead
                    if (rule.rule_type === "template_table") return false;
                    if (rule.rule_type === "template_text" && ruleParams.source_chunk_ids && Array.isArray(ruleParams.source_chunk_ids)) return false;
                    // template_text without any source reference is not clickable
                    if (rule.rule_type === "template_text" && !item.url && !ruleParams.source_chunk_id) return false;
                    return true;
                  };

                  // Get the document name - use pre-populated data from aiSteps if available
                  const getDocumentName = () => {
                    if (rule.document_name) {
                      return rule.document_name;
                    }
                    // Fallback for rules without aiSteps data
                    if (rule.rule_type === "secondary_tfl_reference") {
                      return documentName || `TFL ${ruleParams.tfl_id}` || "TFL Reference";
                    } else if (rule.rule_type === "literal_content_rule") {
                      return "Literal Content";
                    } else if (rule.rule_type === "placeholder_rule") {
                      return "Placeholder";
                    } else if (rule.rule_type === "intra_document_summarize") {
                      return "Document Summary";
                    } else if (rule.rule_type === "tfl_table_and_summary") {
                      return documentName || `Table ${ruleParams.tfl_id}` || "Table and Summary";
                    } else if (rule.rule_type === "template_table") {
                      return documentName || "Template Table";
                    } else if (rule.rule_type === "ncr_intro_methods_conclusions") {
                      return documentName || `NCR ${ruleParams.ncr_id}` || "NCR Reference";
                    } else if (["literal_summarize_rule", "copy_paste_rule", "copy_paste", "summarize_content", "fill_in_the_blank", "fill_in_blank"].includes(rule.rule_type)) {
                      if (ruleParams.source_document_id) {
                        return documentName || `Document ${idx + 1}`;
                      }
                      return `Source Document ${idx + 1}`;
                    } else if (rule.rule_type === "template_text_rule" || rule.rule_type === "template_text") {
                      return documentName || `Template Text ${idx + 1}`;
                    }
                    return `Document ${idx + 1}`;
                  };
                  

                  // Get section title - use pre-populated data from aiSteps if available
                  const getSectionTitle = () => {
                    if (rule.section_name) {
                      return rule.section_name;
                    }
                    // Fallback for rules without aiSteps data
                    if (rule.rule_type === "secondary_tfl_reference") {
                      // Extract only the table number (e.g., "Table 2.1.2.1")
                      const tableNumberMatch = documentName ? documentName.match(/^[T|t]able\s+(\d+(?:\.\d+)*\.?)/) : null;
                      const sectionName = tableNumberMatch ? `Table ${tableNumberMatch[1]}` : documentName;
                      return sectionName || ruleParams.referenced_table_title || `TFL ${ruleParams.tfl_id}`;
                    } else if (rule.rule_type === "intra_document_summarize") {
                      return "";
                    } else if (rule.rule_type === "placeholder_rule") {
                      return "";
                    } else if (rule.rule_type === "tfl_table_and_summary") {
                      // Extract only the table number (e.g., "Table 2.1.2.1")
                      const tableNumberMatch = documentName ? documentName.match(/^[T|t]able\s+(\d+(?:\.\d+)*\.?)/) : null;
                      const sectionName = tableNumberMatch ? `Table ${tableNumberMatch[1]}` : documentName;
                      return sectionName || `Table ${ruleParams.tfl_id}` || "Table and Summary";
                    } else if (rule.rule_type === "template_table") {
                      const chunkCount = ruleParams.source_chunk_ids?.length || 0;
                      return `${chunkCount} source${chunkCount !== 1 ? 's' : ''}`;
                    } else if (rule.rule_type === "ncr_intro_methods_conclusions") {
                      console.log('[getSectionTitle] NCR Intro Methods Conclusions Section Title:', ruleParams.section_title);
                      return ruleParams.section_title || `NCR ${ruleParams.ncr_id}` || "NCR Reference";
                    } else if (["literal_summarize_rule", "copy_paste_rule", "copy_paste"].includes(rule.rule_type)) {
                      return item.sectionName || `Source section`;
                    } else if (rule.rule_type === "literal_content_rule") {
                      return ruleParams.application_context || "Content section";
                    } else if (rule.rule_type === "summarize_content") {
                      return item.sectionName || "Source section";
                    } else if (rule.rule_type === "fill_in_blank" || rule.rule_type === "fill_in_the_blank") {
                      return item.sectionName || "Fill in the blank";
                    } else if (rule.rule_type === "template_text_rule" || rule.rule_type === "template_text") {
                      return selectedSection?.title || "Template text";
                    }
                    return selectedSection?.title || `Section ${idx + 1}`;
                  };

                  // Get the display text - use chunk_content from aiSteps if available
                  const getDisplayText = () => {
                    if (rule.chunk_content) {
                      // Truncate long content for display
                      return rule.chunk_content.length > 150
                        ? rule.chunk_content.substring(0, 150) + "..."
                        : rule.chunk_content;
                    }
                    // Fallback for rules without aiSteps data
                    if (rule.rule_type === "literal_content_rule") {
                      return ruleParams.content || ruleParams.user_instructions || "No content available";
                    } else if (rule.rule_type === "intra_document_summarize") {
                      return ruleParams.summary_focus || ruleParams.synthesis_type || ruleParams.user_instructions || "Summary focus not specified";
                    } else if (rule.rule_type === "secondary_tfl_reference") {
                      return ruleParams.section_synopsis || ruleParams.rationale || "TFL reference";
                    } else if (rule.rule_type === "literal_summarize_rule") {
                      return ruleParams.summary_focus || ruleParams.user_instructions || "Summarize content from source";
                    } else if (rule.rule_type === "tfl_table_and_summary") {
                      return ruleParams.table_concept_description || ruleParams.user_instructions || "Table and summary from source";
                    } else if (rule.rule_type === "template_table") {
                      return ruleParams.user_instructions || "Template table from multiple sources";
                    } else if (rule.rule_type === "ncr_intro_methods_conclusions") {
                      return ruleParams.notes || "NCR reference";
                    } else if (rule.rule_type === "copy_paste_rule" || rule.rule_type === "copy_paste") {
                      return ruleParams.user_instructions || "Copy content from source document";
                    } else if (rule.rule_type === "summarize_content") {
                      return ruleParams.summary_focus || ruleParams.user_instructions || "Summarize content from source";
                    } else if (rule.rule_type === "fill_in_the_blank") {
                      return ruleParams.user_instructions || "Fill in the blank";
                    }
                    return rule.description || "Rule content";
                  };

                  // Removed cardId and isExpanded - no longer needed for dropdown

                  // Determine the card title - use section name if available, otherwise use rule name
                  const getCardTitle = () => {
                    // For non-clickable rules (like literal_content_rule), use a simple name
                    if (!isClickable(rule.rule_type)) {
                      if (rule.rule_type === "literal_content_rule") {
                        return "Literal Content";
                      } else if (rule.rule_type === "intra_document_summarize") {
                        return "Document Summary";
                      } else if (rule.rule_type === "placeholder_rule") {
                        return "Placeholder";
                      }
                      // Fallback for other non-clickable rules
                      return `Rule ${idx + 1}`;
                    }

                    // For clickable rules, use the existing logic
                    if (rule.section_name) {
                      return rule.section_name;
                    }
                    // Fallback to section title from getSectionTitle
                    const sectionTitle = getSectionTitle();
                    if (sectionTitle && sectionTitle !== '' && sectionTitle !== `Section ${idx + 1}`) {
                      return sectionTitle;
                    }
                    // Otherwise use the rule name or type
                    return getDocumentName() || `Rule ${idx + 1}`;
                  };

                  // Check if rule has PDF file references
                  const getPDFReference = () => {
                    if (rule.source_data_references && rule.source_data_references.length > 0) {
                      const pdfRef = rule.source_data_references.find(ref => ref.url && ref.url.includes('.pdf'));
                      if (pdfRef && pdfRef.url) {
                        const fileName = pdfRef.url.split('/').pop();
                        return fileName;
                      }
                    }
                    return null;
                  };

                  const pdfReference = getPDFReference();

                  const handleCardClick = (event) => {
                    // Only handle click if card is clickable
                    if (!isCardClickable()) {
                      return;
                    }

                    if (isClickable(rule.rule_type)) {
                      // Handle clicks based on rule type
                      if (["literal_summarize_rule", "tfl_table_and_summary", "copy_paste_rule", "secondary_tfl_reference", "ncr_intro_methods_conclusions", "summarize_content", "fill_in_the_blank", "template_text_rule", "template_text", "copy_paste"].includes(rule.rule_type)) {
                        // ALWAYS use getUrl to generate the URL - do not use rule.url
                        const fileUrl = item.url;

                        console.log('Generated fileUrl:', fileUrl);
                        console.log('Rule type:', rule.rule_type);
                        console.log('Rule params:', ruleParams);

                        // Only proceed if we have a URL (template_text rules without source references won't have one)
                        if (fileUrl && onSourceCardClick) {
                          onSourceCardClick({
                            fileUrl: fileUrl,
                            chunkId: rule.source_chunk_id || ruleParams.source_chunk_id,
                            title: getDocumentName(),
                            selectedText: getSectionTitle(),
                            page: page
                          });
                        } else if (!fileUrl) {
                          console.log('No URL available for rule:', rule.rule_type, '- this rule may not reference a source document');
                        }
                      }
                    }
                  };

                  // Handle chip click for template_table
                  const handleChipClick = async (chunkId, event) => {
                    console.log('[handleChipClick] Chip clicked with chunkId:', chunkId);
                    event.stopPropagation();

                    try {
                      // First, try to use pre-fetched data
                      const chunkData = ruleParams.chunks_data?.find(c => c.chunk_id === chunkId);

                      if (chunkData && chunkData.url) {
                        console.log('[handleChipClick] Using pre-fetched data:', chunkData);

                        if (onSourceCardClick) {
                          console.log('[handleChipClick] Calling onSourceCardClick with pre-fetched data');
                          onSourceCardClick({
                            fileUrl: chunkData.url,
                            chunkId: chunkId,
                            title: chunkData.document_name || 'Source Document',
                            selectedText: chunkData.section_name || '',
                            page: chunkData.page
                          });
                        } else {
                          console.error('[handleChipClick] onSourceCardClick is not defined');
                        }
                      } else {
                        // Fallback: fetch data if not pre-fetched
                        console.log('[handleChipClick] Pre-fetched data not available, fetching...');
                        const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);

                        const fetchedChunkData = await getChunkFromId(
                          () => {}, // setLoading - no-op for chip click
                          () => {}, // setError - no-op for chip click
                          chunkId,
                          orgAccessToken
                        );
                        console.log('[handleChipClick] Fetched chunk data:', fetchedChunkData);

                        if (fetchedChunkData && fetchedChunkData.url && onSourceCardClick) {
                          console.log('[handleChipClick] Calling onSourceCardClick with fetched data');
                          onSourceCardClick({
                            fileUrl: fetchedChunkData.url,
                            chunkId: chunkId,
                            title: fetchedChunkData.document_name || 'Source Document',
                            selectedText: fetchedChunkData.section_name || '',
                            page: fetchedChunkData.page
                          });
                        } else {
                          console.error('[handleChipClick] Missing url or onSourceCardClick:', {
                            hasUrl: !!fetchedChunkData?.url,
                            hasCallback: !!onSourceCardClick
                          });
                        }
                      }
                    } catch (error) {
                      console.error('[handleChipClick] Error handling chip click:', error);
                    }
                  };

                  return (
                    <Card
                      key={idx}
                      onClick={handleCardClick}
                      sx={{
                        background: "#fff",
                        borderRadius: 2,
                        border: "1px solid #E5E7EB",
                        boxShadow: "none",
                        overflow: "visible",
                        cursor: isCardClickable() ? "pointer" : "default",
                        "&:hover": {
                          boxShadow: isCardClickable() ? "0 4px 12px rgba(16,30,54,0.1)" : "0 2px 8px rgba(16,30,54,0.06)",
                          transform: isCardClickable() ? "translateY(-2px)" : "none",
                          transition: "all 0.2s ease"
                        }
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        {/* PDF Reference Header (if exists) */}
                        {pdfReference && (
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1.5,
                            pb: 1.5,
                            borderBottom: "1px solid #F1F5F9"
                          }}>
                            <Box sx={{
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0
                            }}>
                              <svg width="16" height="16" fill="none" xmlns="https://www.w3.org/2000/svg">
                                <path d="M14 4.5V14a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1h7.5L14 4.5z" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M10 1v4h4" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <text x="5" y="10" fontSize="5" fill="#DC2626" fontWeight="600">PDF</text>
                              </svg>
                            </Box>
                            <Typography sx={{
                              fontSize: 13,
                              color: "#64748B",
                              flex: 1
                            }}>
                              {pdfReference}
                            </Typography>
                          </Box>
                        )}

                        {/* Always Visible: Source Header with Icon */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                          {/* Icon based on rule type */}
                          <Box sx={{
                            width: 20,
                            height: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                          }}>
                            {(() => {
                              // Choose icon based on rule type
                              if (rule.rule_type === "secondary_tfl_reference") {
                                // Table icon for TFL
                                return (
                                  <svg width="16" height="16" fill="none" xmlns="https://www.w3.org/2000/svg">
                                    <rect x="2" y="2" width="12" height="12" rx="1" stroke="#9333EA" strokeWidth="1.5"/>
                                    <path d="M2 5.5h12M5.5 2v12M10.5 2v12" stroke="#9333EA" strokeWidth="1.5"/>
                                  </svg>
                                );
                              } else if (["literal_content_rule", "intra_document_summarize"].includes(rule.rule_type)) {
                                // Text icon for content rules
                                return (
                                  <svg width="16" height="16" fill="none" xmlns="https://www.w3.org/2000/svg">
                                    <path d="M4 4h8M4 8h8M4 12h5" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                                  </svg>
                                );
                              } else {
                                // Document icon for source-based rules
                                return (
                                  <svg width="16" height="16" fill="none" xmlns="https://www.w3.org/2000/svg">
                                    <path d="M14 4.5V14a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1h7.5L14 4.5z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 1v4h4" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                );
                              }
                            })()}
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#1E293B",
                              lineHeight: 1.4
                            }}>
                              {getDocumentName()}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Unified Container for Section and Content */}
                        {/* Section Title and Generated Content */}
                        <Box
                          sx={{
                            background: "#F3F4F6",
                            borderRadius: 1,
                            mb: 2
                          }}
                        >
                          {/* Section Title */}
                          <Box sx={{
                            px: 1.5,
                            pt: 1.5,
                            pb: 1
                          }}>
                            <Typography sx={{
                              fontSize: 14,
                              // fontWeight: 600,
                              color: "#1E293B",
                              letterSpacing: "-0.01em"
                            }}>
                              {getCardTitle()}
                            </Typography>
                          </Box>

                          {/* Generated Content - 2 lines max */}
                          <Box sx={{
                            px: 1.5,
                            pb: 1.5,
                            pt: 0.5
                          }}>
                            <Typography sx={{
                              fontSize: 13,
                              color: "#475569",
                              lineHeight: 1.6,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}>
                              {item.generatedContent || rule.generatedContent || getDisplayText()}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Action chip - Always Visible */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#64748B" }}>Action</Typography>
                          <Tooltip
                            title={item.explanation || "No explanation available"}
                            arrow
                            placement="bottom-start"
                            PopperProps={{
                              modifiers: [
                                {
                                  name: 'flip',
                                  enabled: true,
                                  options: {
                                    fallbackPlacements: ['top-start'],
                                  },
                                },
                              ],
                            }}
                          >
                            <Chip
                              label={getActionLabel(rule.rule_type)}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: 12,
                                fontWeight: 500,
                                backgroundColor: isClickable(rule.rule_type) ? "#EFF6FF" : "#F3F4F6",
                                color: isClickable(rule.rule_type) ? "#2563EB" : "#6B7280",
                                border: isClickable(rule.rule_type) ? "1px solid #DBEAFE" : "1px solid #E5E7EB",
                                cursor: "help",
                                "& .MuiChip-label": {
                                  px: 1.5
                                },
                                "&:hover": isClickable(rule.rule_type) ? {
                                  backgroundColor: "#DBEAFE"
                                } : {}
                              }}
                            />
                          </Tooltip>
                        </Box>

                        {/* Source Chips for template_table and template_text */}
                        {(console.log('[Chip Render Check] rule.rule_type:', rule.rule_type, 'source_chunk_ids:', ruleParams.source_chunk_ids), (rule.rule_type === "template_table" || rule.rule_type === "template_text") && ruleParams.source_chunk_ids && Array.isArray(ruleParams.source_chunk_ids)) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontSize: 12, color: "#64748B", mb: 1 }}>Sources</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {ruleParams.source_chunk_ids.map((chunkId, chipIdx) => {
                                console.log('[Rendering Chip] chipIdx:', chipIdx, 'chunkId:', chunkId);
                                return (
                                <Chip
                                  key={chipIdx}
                                  label={`Source ${chipIdx + 1}`}
                                  size="small"
                                  onClick={(e) => {
                                    console.log('[Chip onClick] Triggered for chipIdx:', chipIdx, 'chunkId:', chunkId);
                                    handleChipClick(chunkId, e);
                                  }}
                                  sx={{
                                    height: 24,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    backgroundColor: "#F0FDF4",
                                    color: "#15803D",
                                    border: "1px solid #BBF7D0",
                                    cursor: "pointer",
                                    "& .MuiChip-label": {
                                      px: 1.5
                                    },
                                    "&:hover": {
                                      backgroundColor: "#DCFCE7",
                                      transform: "translateY(-1px)",
                                      transition: "all 0.2s ease"
                                    }
                                  }}
                                />
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  );
                })
              ) : (
                /* Show placeholder if no rules or no section selected */
                <Typography sx={{ color: "#94A3B8", fontSize: 14, textAlign: "center", py: 4 }}>
                  {!selectedSection
                    ? "Please select a section to view its rules"
                    : "No rules available for this section."}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Fixed footer for Open Template button - ALWAYS VISIBLE */}
          <Box sx={{
            background: "#fff",
            borderTop: "1px solid #E5E7EB",
            p: 2,
            boxShadow: "0 -2px 4px rgba(0, 0, 0, 0.05)",
            flexShrink: 0, // Don't shrink - always visible
          }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={onOpenTemplateButtonClick}
              sx={{
                textTransform: "none",
                py: 1.5,
                borderColor: "#E5E7EB",
                color: "#374151",
                fontSize: 16,
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#D1D5DB",
                  background: "#F9FAFB"
                }
              }}
              startIcon={<Inventory2RoundedIcon sx={{ fontSize: 18 }} />}
            >
              Open template
            </Button>
          </Box>
        </Box>
      )}
      {activeTab === "edit" && (
        <Box sx={{
          flex: 1, // Fill remaining height after tabs
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#fff",
        }}>
          {/* No Section dropdown in Edit tab - content starts immediately */}
          {/* Scrollable content area for Edit tab */}
          <Box sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            background: "#F8FAFC",
            p: 2,
          }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    minWidth: 0
                }}
            >
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                backgroundColor: '#F5F5F5',
                borderRadius: '8px',
                padding: '2px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >   
              {/* Tabs */}
              <Tabs
                value={sectionFormat}
                onChange={handleFormatChange}
                aria-label="Section format"
                slotProps={{ indicator: { style: { display: 'none' } } }}
                variant="fullWidth"
                sx={{
                  width: '100%',
                  height: '32px',
                  padding: '2px',
                  minHeight: 'unset',
                  position: 'relative',
                  zIndex: 2,
                  borderBottom: 'none !important',
                  backgroundColor: '#F3F4F6',
                  '&::before': {
                      display: 'none !important',
                      borderBottom: 'none !important'
                  },
                  '&.MuiTabs-root': {
                      borderBottom: 'none !important',
                      '&::before': {
                          display: 'none !important'
                      }
                  },
                  '& .MuiTabs-scroller': {
                      borderBottom: 'none !important',
                      '&::before': {
                          display: 'none !important',
                          borderBottom: 'none !important'
                      },
                      '&::after': {
                          display: 'none !important',
                          borderBottom: 'none !important'
                      }
                  },
                  '& .MuiTabs-flexContainer': {
                      width: '100%',
                      display: 'flex',
                      gap: '4px',
                      backgroundColor: '#F3F4F6',
                      borderBottom: 'none !important',
                      '&::before': {
                          display: 'none !important',
                          borderBottom: 'none !important'
                      },
                      '&::after': {
                          display: 'none !important',
                          borderBottom: 'none !important'
                      }
                  },
                  '& .MuiTab-root': {
                      flex: 1,
                      minHeight: 'unset',
                      padding: '9px 25px',
                      color: '#374151',
                      fontSize: '14px',
                      lineHeight: '20px',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '6px',
                      transition: 'color 0.2s ease, font-weight 0.2s ease',
                      backgroundColor: 'transparent',
                      position: 'relative',
                      zIndex: 2,
                      height: '28px',
                      '&:hover': {
                          backgroundColor: 'transparent'
                      }
                  },
                  '& .MuiTab-root.Mui-selected': {
                      color: '#111827',
                      lineHeight: '20px',
                      fontSize: '14px',
                      fontWeight: 600,
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      boxSizing: 'border-box',
                      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)'
                  }
                }}
              >
                  <Tab label="Content" />
                  <Tab label="Source Document" />
              </Tabs>
            </Box>
          </Box>
          
          { sectionFormat === 0 && (
            // Writing Tools content
            <Box sx={{
              background: "#F8FAFC",
              px: 2,
              py: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              // height: "calc(100vh - 150px)",
            }}>
              <Box sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                flex: 1,
                overflowY: "auto"
              }}>
                {/* Quick Actions Section */}
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 2, color: "#1E293B" }}>
                    Quick Actions
                  </Typography>

                  {/* Length Row */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography sx={{ fontSize: 14, color: "#64748B", minWidth: 80, fontWeight: 600 }}>
                      Length
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedText.trim() || loading}
                        onClick={() => regenerateSelectionWithPrompt("Make this text longer.")}
                        sx={{
                          width: "100%",
                          borderRadius: 1.2,
                          fontWeight: 500,
                          fontSize: 14,
                          borderColor: "#E2E8F0",
                          color: "#475569",
                          background: "#fff",
                          px: 2.5,
                          "&:hover": {
                            borderColor: "#CBD5E1",
                            background: "#F8FAFC"
                          },
                          "&:disabled": {
                            borderColor: "#E2E8F0",
                            color: "#CBD5E1",
                            background: "#F8FAFC"
                          }
                        }}
                      >
                        Longer
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedText.trim() || loading}
                        onClick={() => regenerateSelectionWithPrompt("Make this text shorter.")}
                        sx={{
                          width: "100%",
                          borderRadius: 1.2,
                          fontWeight: 500,
                          fontSize: 14,
                          borderColor: "#E2E8F0",
                          color: "#475569",
                          background: "#fff",
                          px: 2.5,
                          "&:hover": {
                            borderColor: "#CBD5E1",
                            background: "#F8FAFC"
                          },
                          "&:disabled": {
                            borderColor: "#E2E8F0",
                            color: "#CBD5E1",
                            background: "#F8FAFC"
                          }
                        }}
                      >
                        Shorter
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography sx={{ fontSize: 14, color: "#64748B", minWidth: 80, fontWeight: 600 }}>
                      Tense
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedText.trim() || loading}
                        onClick={() => regenerateSelectionWithPrompt("Change this text to present tense.")}
                        sx={{
                          width: "100%",
                          borderRadius: 1.2,
                          fontWeight: 500,
                          fontSize: 14,
                          borderColor: "#E2E8F0",
                          color: "#475569",
                          background: "#fff",
                          px: 2.5,
                          py: 0.8,
                          textTransform: "none",
                          minWidth: 80,
                          "&:hover": { 
                            background: "#F8FAFC", 
                            borderColor: "#CBD5E1" 
                          },
                        }}
                      >
                        Present
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedText.trim() || loading}
                        onClick={() => regenerateSelectionWithPrompt("Change this text to past tense.")}
                        sx={{
                          width: "100%",
                          borderRadius: 1.2,
                          fontWeight: 500,
                          fontSize: 14,
                          borderColor: "#E2E8F0",
                          color: "#475569",
                          background: "#fff",
                          px: 2.5,
                          py: 0.8,
                          textTransform: "none",
                          minWidth: 60,
                          "&:hover": { 
                            background: "#F8FAFC", 
                            borderColor: "#CBD5E1" 
                          },
                        }}
                      >
                        Past
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedText.trim() || loading}
                        onClick={() => regenerateSelectionWithPrompt("Change this text to future tense.")}
                        sx={{
                          width: "100%",
                          borderRadius: 1.2,
                          fontWeight: 500,
                          fontSize: 14,
                          borderColor: "#E2E8F0",
                          color: "#475569",
                          background: "#fff",
                          px: 2.5,
                          py: 0.8,
                          textTransform: "none",
                          minWidth: 70,
                          "&:hover": { 
                            background: "#F8FAFC", 
                            borderColor: "#CBD5E1" 
                          },
                        }}
                      >
                        Future
                      </Button>
                    </Box>
                  </Box>
                    
                  {/* Insert Row */}
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography sx={{ fontSize: 14, color: "#64748B", minWidth: 80, fontWeight: 600 }}>
                      Insert
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={loading}
                      onClick={() => setAddContentModalOpen(true)}
                      sx={{
                        width: "100%",
                        borderRadius: 1.2,
                        fontWeight: 500,
                        fontSize: 14,
                        backgroundColor: '#EEF2FF',
                        color: "#4338CA",
                        border: 'solid 1px #E0E7FF',
                        px: 2.5,
                        py: 0.8,
                        textTransform: "none",
                        boxShadow: "none",
                        '&:hover': {
                          backgroundColor: '#E0E7FF',
                          boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      + Add Content
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ 
                    textAlign: "center", 
                    position: "relative",
                    mb: 2,
                    mt: 1
                  }}>
                    <Divider sx={{ borderColor: "#E2E8F0" }} />
                    <Typography 
                      sx={{ 
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "#F8FAFC",
                        px: 2,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#64748B",
                        letterSpacing: 0.5
                      }}
                    >
                      CUSTOM
                    </Typography>
                  </Box>
                  
                  {/* Custom Instructions Section */}
                  <Box>
                    <Typography sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      mb: 1.5,
                      color: "#64748B",
                      letterSpacing: 0.3
                    }}>
                      Custom Instructions
                    </Typography>
                    <TextField
                      multiline
                      minRows={4}
                      maxRows={6}
                      fullWidth
                      placeholder="Describe the changes you want to make..."
                      value={regenerateText}
                      onChange={e => setRegenerateText(e.target.value)}
                      sx={{
                        background: "#fff",
                        borderRadius: 2,
                        mb: 2,
                        "& .MuiInputBase-root": {
                          fontSize: 14,
                          color: "#334155",
                          borderRadius: 2,
                          border: "1px solid #E2E8F0",
                          boxShadow: "none",
                          px: 2,
                          py: 1.5,
                        },
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        "&:hover .MuiInputBase-root": { borderColor: "#CBD5E1" },
                        "& .MuiInputBase-input::placeholder": {
                          color: "#94A3B8",
                          opacity: 1
                        }
                      }}
                      InputProps={{
                        sx: { p: 0 }
                      }}
                    />

                    {/* Info message */}
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#94A3B8",
                      fontSize: 14
                    }}>
                      <svg width="20" height="20" fill="none" xmlns="https://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="9" stroke="#CBD5E1" strokeWidth="1.5"/>
                        <text x="10" y="14" textAnchor="middle" fontSize="12" fill="#94A3B8" fontFamily="Arial">i</text>
                      </svg>
                      <Typography sx={{ fontSize: 14, color: "#94A3B8" }}>
                        Select text in the editor to enable actions
                      </Typography>
                    </Box>
                  </Box>
              </Box>

              {/* Button area removed - moved to fixed footer */}
            </Box>
          )}

          { sectionFormat === 1 && (
            // Source Document content
            <Box sx={{ background: "#F8FAFC", minHeight: "100vh", width: "100%", px: 0, py: 0 }}>
              {/* Document Sources Card */}
              <Box
                sx={{
                  background: "#F8FAFC",
                  borderRadius: 2,
                  mx: 2,
                  mt: 0,
                  mb: 2,
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, flex: 1 }}>
                    Source documents · {combinedDocumentSources.length + mrtTables.length} 
                  </Typography>

                  {/* <Tooltip title="Cancel all active polling and reset statuses to waiting">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelAllSourceDocumentPollingLocal();
                      }}
                      disabled={getActivePollingSourceDocuments().length === 0}
                      sx={{
                        color: getActivePollingSourceDocuments().length > 0 ? "#DC2626" : "#9CA3AF",
                        background: "transparent",
                        mr: 1,
                        "&:hover": { 
                          background: getActivePollingSourceDocuments().length > 0 ? "#FEE2E2" : "#F3F4F6" 
                        },
                        "&:disabled": {
                          color: "#9CA3AF"
                        }
                      }}
                    >
                      <StopIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Debug polling status (check console)">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDebugSourceDocumentPollingLocal();
                      }}
                      sx={{
                        color: "#8B5CF6",
                        background: "transparent",
                        mr: 1,
                        "&:hover": { 
                          background: "#F3F4F6" 
                        }
                      }}
                    >
                      <BugReportIcon />
                    </IconButton>
                  </Tooltip> */}
                  
                  {/* <Tooltip title="Add document source">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        setAddDocSourceModalOpen(true);
                      }}
                      sx={{
                        color: "#2563EB",
                        background: "transparent",
                        ml: 1,
                        "&:hover": { background: "#EEF2FF" }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip> */}
                </Box>
                {/* Document Source Cards */}
                {combinedDocumentSources.map((item) => {
                  const docStatus = getSourceDocumentStatusLocal(item.document_id)
                  return (
                    <SectionSourceCard
                      key={item.document_id}
                      fileName={item.document_name}
                      onDelete={() => handleDeleteSourceDocumentLocal(item.document_id)}
                      onRefresh={() => handleUpdateSourceDocumentLocal(item.document_id)}
                      onClick={async () => await onSourceCardClickSourceTab(docStatus, item)}
                      date_uploaded={dateUploaded}
                      showRetrievedInfo={true}
                      uploading={docStatus === "uploading"}
                      needReview={docStatus === "review"}
                      disableActions={docStatus !== "waiting"}
                      onResetToWaiting={handleResetToWaiting}
                      documentId={item.document_id}
                      onForceReview={handleForceReview}
                    />
                  )
                })}
                {mrtTables.map((item) => {
                  const docStatus = getSourceDocumentStatusLocal(item.document_id)
                  return (
                    <SectionSourceCard
                      key={item.document_id}
                      fileName={item.document_name}
                      onDelete={() => handleDeleteMRTTable(item.document_id)}
                      onRefresh={() => handleUpdateSourceDocumentLocal(item.document_id)}
                      onClick={async () => await onSourceCardClickSourceTab(docStatus, item)}
                      date_uploaded={dateUploaded}
                      showRetrievedInfo={true}
                      uploading={docStatus === "uploading"}
                      needReview={docStatus === "review"}
                      disableActions={docStatus !== "waiting"}
                      onResetToWaiting={handleResetToWaiting}
                      documentId={item.document_id}
                      onForceReview={handleForceReview}
                    />
                  )
                })}
              </Box>
              
              {/* Add Document Source Flow */}
              <UploadNewFileModal
                titleText="Add New Document Source"
                open={addDocSourceModalOpen}
                onClose={() => setAddDocSourceModalOpen(false)}
                onFileSelect={handleFileSelect}
                setLoading={setLoading}
                orgAccessToken={authInfo.tokens.getAccessTokenForOrg(org)}
                fileRef={fileInputRef}
              />

              {/* Replace Document Source Flow */}
              <UploadNewFileModal
                titleText="Replace Document Source"
                open={updateSourceDocumentModalOpen !== null}
                onClose={() => setUpdateSourceDocumentModalOpen(null)}
                onFileSelect={handleFileSelect}
                onSourceSelectClick={() => setSourceModalOpen(true)}
                setLoading={setLoading}
                orgAccessToken={authInfo.tokens.getAccessTokenForOrg(org)}
                fileRef={fileInputRef}
              />

              {/* Assign Tables Modal - Only show for TFL documents */}
              <AssignTablesModal
                open={currentReviewingSourceDocument !== null && !sectionSelectionModalOpen}
                handleClose={handleCloseModals}
                onInsertAll={async (tableMappings) => await handleInsertAllCompleteLocal(currentReviewingSourceDocument, tableMappings)}
                documentMRTTables={documentMRTTables}
                sourceDocument={currentReviewingSourceDocument}
                connectorId={connectorId}
              />

              {/* Section Selection Modal - Only show for source documents */}
              <SectionSelectionModal
                documentId={id}
                open={sectionSelectionModalOpen && currentReviewingSourceDocument !== null}
                onClose={handleCloseModals}
                onInsertAll={(selectedSectionIds) => handleConfirmSectionsToRegenerate(currentReviewingSourceDocument, selectedSectionIds)}
                templateName={templateNickname}
                sourceDocument={currentReviewingSourceDocument}
              />

              {/* Source Selection Modal */}
              <SourceSelectionModal
                open={sourceModalOpen}
                handleClose={handleSourceModalClose}
                onSourceSelect={handleSourceSelect}
                documentName={documentName}
                productName={productName}
                templateType={templateType}
                outline={outlineSections}
                id={id}
                setError={setError}
                orgId={org}
                org_name={orgs[0].orgName}
                selectedFileNames={selectedCloudFileNames}
                initialSelectedFiles={selectedCloudFiles.map((file) => ({
                  ...file
                }))}
                getOrgAccessToken={() => getOrgAccessToken(orgs[0].orgId)}
                // allowedFolders={['input', 'output', 'templates']}
                allowedFolders={['input']}
                allowedSelectedFileTypes={['pdf', 'docx', 'doc', 'txt', 'csv', 'xls', 'xlsx', 'ppt', 'pptx']}
              />
            </Box>
          )}
          </Box>
          {/* Fixed footer for Regenerate button - ALWAYS VISIBLE when there's text */}
          {(regenerateText.trim() || loading) && (
            <Box sx={{
              background: "#fff",
              borderTop: "1px solid #E5E7EB",
              p: 2,
              boxShadow: "0 -2px 4px rgba(0, 0, 0, 0.05)",
              flexShrink: 0, // Don't shrink - always visible
            }}>
              {/* Regenerate Button - styled to match Open Template button */}
              {regenerateText.trim() && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={!regenerateText.trim() || !selectedText.trim() || loading}
                  onClick={() => regenerateSelection()}
                  sx={{
                    textTransform: "none",
                    py: 1.5,
                    borderColor: "#E5E7EB",
                    color: loading ? "transparent" : "#374151",
                    fontSize: 16,
                    fontWeight: 500,
                    position: "relative",
                    "&:hover": {
                      borderColor: "#D1D5DB",
                      background: "#F9FAFB"
                    },
                    "&.Mui-disabled": {
                      borderColor: "#E5E7EB",
                      background: "transparent",
                      color: loading ? "transparent" : "#9CA3AF"
                    }
                  }}
                  startIcon={!loading ? <CachedRoundedIcon sx={{ fontSize: 20 }} /> : null}
                >
                  {loading ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-10px",
                        marginLeft: "-10px",
                        color: "#374151"
                      }}
                    />
                  ) : (
                    "Regenerate Text"
                  )}
                </Button>
              )}

              {/* Loading state when no regenerate text but loading */}
              {!regenerateText.trim() && loading && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 1.5
                }}>
                  <CircularProgress size={24} sx={{ color: "#374151" }} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      <input
        type="file"
        multiple
        accept=".pdf,.docx,.xlsx,.csv,.rtf"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {addContentModalOpen && (
        <InsertTableModal
          open={addContentModalOpen}
          onClose={() => setAddContentModalOpen(false)}
          tables={mrtTablesList}
          reports={mrtReportsList} // Pass empty array for now, will be populated later
          onInsert={(selectedItems, type) => {
            if (type === "tables") {
              const tableTitles = selectedItems.map(table => table.table_title).join(", ");
              regenerateSelectionWithPrompt(`Add ${tableTitles} to the document. Include the table title and all footnotes.`);
            } else if (type === "reports") {
              const studyIdentifiers = selectedItems.map(report => report.study_identifier).join(", ");
              regenerateSelectionWithPrompt(`Add ${studyIdentifiers} to the document. Include the study identifier and all relevant content.`);
            }
            setAddContentModalOpen(false);
          }}
        />
      )}
    </Box>
  );
}

export function UploadNewFileModal({ 
  titleText,
  open, 
  onClose, 
  onFileSelect,
  onSourceSelectClick,
  setLoading, 
  orgAccessToken, 
  fileRef, 
}) {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files)
    const fileContents = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = (e) => resolve(e.target.result)
            reader.readAsArrayBuffer(file)
          })
      )
    )

    handleFileUpload(files, fileContents)
  }

  const handleFileUpload = async (files, fileContents) => {
    try {
      const accessToken = orgAccessToken;
      const currentFolder = "input";

      await uploadMultipleFilesUpdated(
        setLoading,
        files.map((file, index) => ({
          name: file.name,
          content: fileContents[index],
        })),
        fileContents,
        (error) => {
          console.error('Upload error:', error)
        },
        currentFolder,
        accessToken
      )
    } catch (error) {
      console.error('Upload failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "100vh",
        zIndex: 1300
      }}
    >
      <Box
        sx={{
          background: "#F9FAFB",
          borderRadius: 3,
          border: '1px solid #FFFFFF',
          boxShadow: "0 4px 24px rgba(16,30,54,0.18)",
          p: 4,
          width: "924px",
          height: "720px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative"
        }}
        onClick={e => e.stopPropagation()}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", gap: 1, mb: 2 }}>
          <IconButton
            onClick={onClose}
            sx={{
              width: '30px',
              height: '30px',
              ml: '-7px',
              '&:hover': {
                backgroundColor: 'transparent',
              }
            }}
          >
              <CloseIcon sx={{ width: '16px', height: '16px', color: '#111827' }} />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: 24, mb: 2 }}>
            {titleText}
          </Typography>
        </Box>
        <Card sx={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          minWidth: '736px',
          mb: 3,
        }}>
          <CardContent
            onClick={(e) => {
              e.stopPropagation()
              fileRef.current?.click()
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: '736px', backgroundColor: '#fff', borderRadius: '8px' }}>
              {/* File Upload Area */}
              <Box>
                <Box
                  sx={{
                    border: '2px dashed #E5E7EB',
                    borderRadius: '8px',
                    padding: '24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#D1D5DB',
                      backgroundColor: '#F9FAFB',
                    },
                    backgroundColor: isDragging ? '#F9FAFB' : 'transparent',
                  }}
                >
                  <UploadFileIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                  <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 1 }}>
                    Click to upload source files
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                    PDF, DOC, DOCX up to 10MB
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '768px', mb: 2 }}>
          <Divider sx={{ borderColor: "#E5E7EB", width: '100%' }}>or</Divider>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '768px', mb: 1 }}>
          <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500}}>
            Select from a workspace library
          </Typography>
        </Box>
        <Card sx={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          minWidth: '736px',
          mb: 3,
        }}>
          <CardContent
            onClick={onSourceSelectClick}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Box
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 3, 
                minWidth: '736px', 
                borderRadius: '8px', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 2,
                pb: '8px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '40px',
                    border: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <img src={logo} alt="Artos" style={{ width: '24px', height: '24px' }} />
                </Box>
                
                {/* Section Title */}
                <Typography
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'inherit',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {'Artos'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Modal>
  )
}


/**
 * RegenerateButton - Figma-style button with loading, disabled, and hover states.
 * DEPRECATED: Now using standard MUI Button to match Open Template button styling
 */
// function RegenerateButton({ disabled, onClick, loading }) {
//   return (
//     <AsynchronousActionButton
//       fullWidth={true}
//       onClick={onClick}
//       loading={loading}
//       disabled={disabled}
//       startIcon={loading ? null : <CachedIcon fontSize="large" />}
//       sx={{
//         background: disabled || loading ? "#CBD5E1" : "#1F477D",
//         color: "#fff",
//         fontWeight: 700,
//         fontSize: "16px",
//         lineHeight: "24px",
//         borderRadius: "6px",
//         textTransform: "none",
//         boxShadow: "none",
//         minHeight: 50,
//         minWidth: 368,
//         transition: "background 0.15s",
//         "&:hover": {
//           background: !disabled && !loading ? "#1E40AF" : "#CBD5E1",
//         },
//         "& .MuiCircularProgress-root": {
//           color: "#fff",
//         },
//       }}
//     >
//       {loading ? "" : "Regenerate Text"}
//     </AsynchronousActionButton>
//   );
// }

export default SectionTabComponent;
